import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import NavBar from '../../../components/NavBar';

import {
	requestBibliotecaDocumentosAllList,
	requestBibliotecaCategoriasList,
	requestBibliotecaTemasList,
	requestBibliotecaRepositorios,
} from '../../../services/requests';
import lapisIcone from '../../../images/lapis-icone.png';

export default function GestaoBiblioteca() {
	const navigate = useNavigate();
	const [documentos, setDocumentos] = useState([]);
	const [loading, setLoading] = useState(true);

	// Obter token do usuário logado
	const getUserToken = () => {
		const userCookie = Cookies.get('user');
		if (userCookie) {
			const user = JSON.parse(userCookie);
			return user.token;
		}
		return null;
	};

	// Filtros
	const [titulo, setTitulo] = useState('');
	const [mes, setMes] = useState('');
	const [ano, setAno] = useState('');
	const [ativo, setAtivo] = useState('');
	const [categoria, setCategoria] = useState('');
	const [tema, setTema] = useState('');
	const [localDocumento, setLocalDocumento] = useState('');
	const [publico, setPublico] = useState('');

	// Novos estados para listas de categorias e temas
	const [categoriaList, setCategoriaList] = useState([]);
	const [temaList, setTemaList] = useState([]);
	const [repoList, setRepoList] = useState([]);

	// Estados para paginação
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [totalCount, setTotalCount] = useState(0);
	const [totalPages, setTotalPages] = useState(0);
	const [showAlert, setShowAlert] = useState();
	const [paginatedDocs, setPaginatedDocs] = useState([]);

	useEffect(() => {
		async function loadDocs() {
			try {
				const token = getUserToken();

				// Verifica se algum filtro foi preenchido
				const algumFiltro =
					titulo ||
					mes ||
					ano ||
					ativo ||
					publico ||
					categoria ||
					tema ||
					localDocumento;

				let params = {
					page: currentPage,
					page_size: pageSize,
					...(algumFiltro
						? {
								search: titulo,
								mes,
								ano,
								// Envia ativo como booleano se preenchido
								...(ativo !== ''
									? { ativo: ativo === 'true' }
									: {}),
								publico: publico,
								categorias: categoriaList.find(
									(item) => item.nome === categoria
								)?.id,
								temas: temaList.find(
									(item) => item.nome === tema
								)?.id,
								repositorios: repoList.find(
									(item) => item.nome === localDocumento
								)?.id,
						  }
						: {}),
				};

				const totalDocuments = await requestBibliotecaDocumentosAllList(
					Object.fromEntries(
						Object.entries({
							...(algumFiltro
								? {
										search: titulo,
										mes,
										ano,
										publico: publico,
										// Envia ativo como booleano se preenchido
										...(ativo !== ''
											? { ativo: ativo === 'true' }
											: {}),
										categorias: categoriaList.find(
											(item) => item.nome === categoria
										)?.id,
										temas: temaList.find(
											(item) => item.nome === tema
										)?.id,
										repositorios: repoList.find(
											(item) =>
												item.nome === localDocumento
										)?.id,
								  }
								: {}),
						}).filter(([_, v]) => v !== '' && v !== null)
					),
					token
				);

				const lista = await requestBibliotecaDocumentosAllList(
					Object.fromEntries(
						Object.entries(params).filter(
							([_, v]) => v !== '' && v !== null
						)
					),
					token
				);

				if (lista && !lista.error) {
					setDocumentos(
						Array.isArray(lista.results) ? lista.results : []
					);
					setTotalCount(totalDocuments.count || 0);
					setTotalPages(Math.ceil((lista.count || 0) / pageSize));
				} else {
					setDocumentos([]);
					setTotalCount(0);
					setTotalPages(0);
				}
			} catch (error) {
				setDocumentos([]);
				setTotalCount(0);
				setTotalPages(0);
			} finally {
				setLoading(false);
			}
		}
		loadDocs();
	}, [
		titulo,
		mes,
		ano,
		ativo,
		categoria,
		tema,
		localDocumento,
		currentPage,
		pageSize,
		categoriaList,
		temaList,
		publico,
		repoList,
	]);

	// Carrega categorias e temas da API para os selects
	useEffect(() => {
		if (Cookies.get('user')) {
			setShowAlert(false);
		} else {
			setShowAlert(true);
		}

		async function loadCategoriasTemas() {
			const token = getUserToken();
			try {
				const catResp = await requestBibliotecaCategoriasList(
					{},
					token
				);
				setCategoriaList(Array.isArray(catResp) ? catResp : []);
			} catch {
				setCategoriaList([]);
			}
			try {
				const temaResp = await requestBibliotecaTemasList({}, token);
				setTemaList(Array.isArray(temaResp) ? temaResp : []);
			} catch {
				setTemaList([]);
			}

			try {
				const repoResp = await requestBibliotecaRepositorios();
				setRepoList(Array.isArray(repoResp) ? repoResp : []);
			} catch {
				setRepoList([]);
			}
		}
		loadCategoriasTemas();
	}, []);

	// Opções únicas para selects
	const anosUnicos = (() => {
		const maxAno = new Date().getFullYear();
		const start = 2020;
		const end = maxAno;
		return Array.from({ length: end - start + 1 }, (_, i) => start + i);
	})();
	const mesesNomes = [
		'Janeiro',
		'Fevereiro',
		'Março',
		'Abril',
		'Maio',
		'Junho',
		'Julho',
		'Agosto',
		'Setembro',
		'Outubro',
		'Novembro',
		'Dezembro',
	];
	const statusUnicos = [
		{ label: 'Ativo', value: 'true' },
		{ label: 'Inativo', value: 'false' },
	];
	const categoriasUnicas = [
		...new Set(documentos.map((doc) => doc.categoria).filter(Boolean)),
	];
	const temasUnicos = [
		...new Set(documentos.map((doc) => doc.tema).filter(Boolean)),
	];

	const repositoriosUnicos = [
		...new Set(
			documentos
				.map((doc) => doc.repositorios)
				.filter((repos) => repos && repos.length === 1)
				.flat()
		),
	];

	// Função para converter número do mês para nome
	function mesNumeroParaNome(numero) {
		const idx = parseInt(numero, 10) - 1;
		return mesesNomes[idx] || numero;
	}

	// Gera lista de meses únicos presentes nos documentos (sempre como nome)
	const mesesUnicos = [
		'Janeiro',
		'Fevereiro',
		'Março',
		'Abril',
		'Maio',
		'Junho',
		'Julho',
		'Agosto',
		'Setembro',
		'Outubro',
		'Novembro',
		'Dezembro',
	];

	// Função para filtrar documentos conforme os filtros selecionados
	const documentosFiltrados = documentos.filter((doc) => {
		// const mesDoc = isNaN(doc.mes) ? doc.mes : mesNumeroParaNome(doc.mes);
		// const anoDoc = doc.ano ? String(doc.ano) : "";
		// Categoria
		const categoriaMatch =
			!categoria ||
			(Array.isArray(doc.categorias)
				? doc.categorias.includes(categoria)
				: doc.categorias === categoria);
		// Tema
		const temaMatch =
			!tema ||
			(Array.isArray(doc.temas)
				? doc.temas.includes(tema)
				: doc.temas === tema);
		return (
			(!titulo ||
				(doc.titulo &&
					doc.titulo.toLowerCase().includes(titulo.toLowerCase()))) &&
			// (!mes || (mesDoc && mesDoc === mes)) &&
			// (!ano || anoDoc === ano) &&
			(ativo === '' || doc.ativo === (ativo === 'true')) &&
			categoriaMatch &&
			temaMatch &&
			(!localDocumento || doc.repositorios.includes(localDocumento)) &&
			(!publico || doc.publico === (publico === 'true'))
		);
	});

	// Funções de paginação
	const handlePageChange = (newPage) => {
		if (newPage >= 1 && newPage <= totalPages) {
			setCurrentPage(newPage);
		}
	};

	const handlePageSizeChange = (newPageSize) => {
		setPageSize(newPageSize);
		setCurrentPage(1); // Volta para a primeira página
	};

	// Função para resetar filtros
	const resetFilters = () => {
		setTitulo('');
		setMes('');
		setAno('');
		setAtivo('');
		setCategoria('');
		setTema('');
		setLocalDocumento('');
		setPublico('');
		setCurrentPage(1);
	};

	return (
		<div className='flex flex-col mx-auto min-h-screen'>
			<NavBar />

			{showAlert ? (
				<section className='bg-[#004A80] mt-[150px] rounded-md w-[90%] sm:w-[70%] md:w-[60%] lg:w-[50%]  mx-auto my-auto'>
					<div className='py-8 px-4 mx-auto max-w-screen-xl lg:py-16 lg:px-6'>
						<div className='mx-auto max-w-screen-sm text-center'>
							<h1 className='mb-4 text-white text-5xl sm:text-6xl lg:text-7xl tracking-tight font-extrabold text-primary-600'>
								401
							</h1>
							<p className='mb-10 text-2xl sm:text-3xl md:text-4xl tracking-tight font-bold text-slate-100'>
								Acesso Negado
							</p>
							<p className='mb-7 text-sm sm:text-base md:text-lg font-light text-gray-300'>
								Você não possui permissão para acessar este
								campo. Entre em contato com o administrador(a)
								da sua unidade para solicitação de acesso.
							</p>
							<a
								href='/login'
								className='inline-flex hover:scale-105 transition-transform text-gray-300 bg-primary-600 hover:bg-primary-800 focus:ring-4 focus:outline-none focus:ring-primary-300 font-medium rounded-lg text-xl px-5 py-2.5 text-center my-4'>
								<span> Volte para a Tela de Login</span>
							</a>
						</div>
					</div>
				</section>
			) : (
				<div className='font-inter flex flex-col flex-grow'>
					<div className='sm:top-[80px] mt-[80px] pb-4 bg-[#f0f4f8] w-full'>
						<div className='w-[90%] sm:w-[95%] mx-auto items-center max-w-[1136px]'>
							<div className='px-[2px] py-[18px]'>
								<p className='mx-auto md:mx-10 text-[#004A80] font-bold text-center text-2xl sm:text-3xl md:text-4xl mt-14 mb-12 font-roboto'>
									Gestão de Arquivos
								</p>
								{/* Filtros */}
								<div className='mb-8'></div>
								<form
									className='mb-8'
									onSubmit={(e) => {
										e.preventDefault();
									}}>
									{/* Mobile (sm:hidden): Nome; Mês+Ano; Status+Repositório; Categoria; Tema; Local */}
									<div className='flex flex-col gap-6 sm:hidden w-full'>
										{/* Nome */}
										<div className='flex flex-col min-w-0'>
											<label
												className='text-[#004A80] font-semibold mb-1 min-w-[60px]'
												htmlFor='titulo'>
												Nome
											</label>
											<input
												id='titulo'
												type='text'
												placeholder='Nome do Documento'
												className='bg-[#F7F9FB] border border-[#E3E3E3] rounded-[8px] w-full px-3 py-1.5 text-[#004A80] outline-none'
												value={titulo}
												onChange={(e) => {
													setCurrentPage(1);
													setTitulo(e.target.value);
												}}
											/>
										</div>

										{/* Mês + Ano na mesma linha */}
										<div className='flex flex-row gap-6 w-full'>
											<div className='flex flex-col flex-1 min-w-0'>
												<label
													className='text-[#004A80] font-semibold mb-1 min-w-[36px]'
													htmlFor='mes'>
													Mês
												</label>

												<select
													id='mes'
													className='bg-[#F7F9FB] border border-[#E3E3E3] rounded-[8px] px-3 py-2 outline-none w-full text-[#004A80]'
													value={mes}
													onChange={(e) => {
														setCurrentPage(1);
														setMes(e.target.value);
													}}>
													<option
														value=''
														disabled
														hidden>
														Selecione o mês
													</option>
													{mesesUnicos.map(
														(m, index) => (
															<option
																key={m}
																value={
																	index + 1
																}>
																{m}
															</option>
														)
													)}
												</select>
											</div>
											<div className='flex flex-col flex-1 min-w-0'>
												<label
													className='text-[#004A80] font-semibold mb-1 min-w-[36px]'
													htmlFor='ano'>
													Ano
												</label>
												<select
													id='ano'
													className='bg-[#F7F9FB] border border-[#E3E3E3] rounded-[8px] px-3 py-2 outline-none w-full text-[#004A80]'
													value={ano}
													onChange={(e) => {
														setCurrentPage(1);
														setAno(e.target.value);
													}}>
													<option
														value=''
														disabled
														hidden>
														Selecione o ano
													</option>
													{anosUnicos.map((a) => (
														<option
															key={a}
															value={a}>
															{a}
														</option>
													))}
												</select>
											</div>
										</div>

										{/* Status + Repositório na mesma linha */}
										<div className='flex flex-row gap-6 w-full'>
											<div className='flex flex-col flex-1 min-w-0'>
												<label
													className='text-[#004A80] font-semibold mb-1 min-w-[36px]'
													htmlFor='status'>
													Status
												</label>
												<select
													id='ativo'
													className='bg-[#F7F9FB] border border-[#E3E3E3] rounded-[8px] px-3 py-2 outline-none w-full text-[#004A80]'
													value={ativo}
													onChange={(e) => {
														setCurrentPage(1);
														setAtivo(
															e.target.value
														);
													}}>
													<option
														value=''
														disabled
														hidden>
														Selecione o status
													</option>
													{statusUnicos.map((s) => (
														<option
															key={s.value}
															value={s.value}>
															{s.label}
														</option>
													))}
												</select>
											</div>
											<div className='flex flex-col flex-1'>
												<label
													className='text-[#004A80] font-semibold mb-1 min-w-[56px]'
													htmlFor='repositorio'>
													Repositório
												</label>
												<select
													id='publico'
													className='bg-[#F7F9FB] border border-[#E3E3E3] rounded-[8px] px-3 py-2 outline-none w-full text-[#004A80]'
													value={publico}
													onChange={(e) => {
														setCurrentPage(1);
														setPublico(
															e.target.value
														);
													}}>
													<option
														value=''
														disabled
														hidden>
														Selecione o repositório
													</option>
													<option value='true'>
														Público
													</option>
													<option value='false'>
														Privado
													</option>
												</select>
											</div>
										</div>

										{/* Categoria (full) */}
										<div className='flex flex-col'>
											<label
												className='text-[#004A80] font-semibold mb-1 min-w-[80px]'
												htmlFor='categoria'>
												Categoria
											</label>
											<select
												id='categoria'
												className='bg-[#F7F9FB] border border-[#E3E3E3] rounded-[8px] px-3 py-2 outline-none w-full text-[#004A80]'
												value={categoria}
												onChange={(e) => {
													setCurrentPage(1);
													setCategoria(
														e.target.value
													);
												}}>
												<option
													value=''
													disabled
													hidden>
													Selecione a categoria
												</option>
												{/* Opções da API */}
												{categoriaList.map((cat) => (
													<option
														key={cat.id}
														value={cat.nome}>
														{cat.nome}
													</option>
												))}
												{/* Opções únicas dos documentos (mantém para compatibilidade) */}
												{categoriasUnicas.map((c) => (
													<option
														key={c}
														value={c}>
														{c}
													</option>
												))}
											</select>
										</div>

										{/* Tema (full) */}
										<div className='flex flex-col'>
											<label
												className='text-[#004A80] font-semibold mb-1 min-w-[56px]'
												htmlFor='tema'>
												Tema
											</label>
											<select
												id='tema'
												className='bg-[#F7F9FB] border border-[#E3E3E3] rounded-[8px] px-3 py-2 outline-none w-full text-[#004A80]'
												value={tema}
												onChange={(e) => {
													setCurrentPage(1);
													setTema(e.target.value);
												}}>
												<option
													value=''
													disabled
													hidden>
													Selecione o tema
												</option>
												{/* Opções da API */}
												{temaList.map((t) => (
													<option
														key={t.id}
														value={t.nome}>
														{t.nome}
													</option>
												))}
												{/* Opções únicas dos documentos (mantém para compatibilidade) */}
												{temasUnicos.map((t) => (
													<option
														key={t}
														value={t}>
														{t}
													</option>
												))}
											</select>
										</div>

										{/* Local do Documento (full) */}
										<div className='flex flex-col'>
											<label
												className='text-[#004A80] font-semibold mb-1 min-w-[56px]'
												htmlFor='localDocumento'>
												Local do Documento
											</label>
											<select
												id='localDocumento'
												className='bg-[#F7F9FB] border border-[#E3E3E3] rounded-[8px] px-3 py-2 outline-none w-full text-[#004A80]'
												value={localDocumento}
												onChange={(e) => {
													setCurrentPage(1);
													setLocalDocumento(
														e.target.value
													);
												}}>
												<option
													value=''
													disabled
													hidden>
													Selecione o local
												</option>

												{repoList
													?.map((obj) => obj?.nome)
													?.map((repo) => (
														<option
															key={repo}
															value={repo}>
															{repo}
														</option>
													))}
											</select>
										</div>
									</div>

									{/* Linha 1 - Desktop (mantida) */}
									<div className='hidden sm:flex sm:flex-row gap-6 mb-6 w-full'>
										<div className='flex flex-col flex-[3] min-w-0'>
											<label
												className='text-[#004A80] font-semibold mb-1 min-w-[60px]'
												htmlFor='titulo'>
												Nome
											</label>
											<input
												id='titulo'
												type='text'
												placeholder='Nome do Documento'
												className='bg-[#F7F9FB] border border-[#E3E3E3] rounded-[8px] w-full px-3 py-1.5 text-[#004A80] outline-none'
												value={titulo}
												onChange={(e) => {
													setCurrentPage(1);
													setTitulo(e.target.value);
												}}
											/>
										</div>
										<div className='flex flex-col flex-1 min-w-0'>
											<label
												className='text-[#004A80] font-semibold mb-1 min-w-[36px]'
												htmlFor='mes'>
												Mês
											</label>
											<select
												id='mes'
												className='bg-[#F7F9FB] border border-[#E3E3E3] rounded-[8px] px-3 py-2 outline-none w-full text-[#004A80]'
												value={mes}
												onChange={(e) => {
													setCurrentPage(1);
													setMes(e.target.value);
												}}>
												<option
													value=''
													disabled
													hidden>
													Selecione o mês
												</option>
												{mesesUnicos.map((m, index) => (
													<option
														key={m}
														value={index + 1}>
														{m}
													</option>
												))}
											</select>
										</div>

										<div className='flex flex-col flex-1 min-w-0'>
											<label
												className='text-[#004A80] font-semibold mb-1 min-w-[36px]'
												htmlFor='ano'>
												Ano
											</label>
											<select
												id='ano'
												className='bg-[#F7F9FB] border border-[#E3E3E3] rounded-[8px] px-3 py-2 outline-none w-full text-[#004A80]'
												value={ano}
												onChange={(e) => {
													setCurrentPage(1);
													setAno(e.target.value);
												}}>
												<option
													value=''
													disabled
													hidden>
													Selecione o ano
												</option>
												{anosUnicos.map((a) => (
													<option
														key={a}
														value={a}>
														{a}
													</option>
												))}
											</select>
										</div>
										{/* <div className="flex flex-col flex-1 min-w-0">
                    <label
                      className="text-[#004A80] font-semibold mb-1 min-w-[36px]"
                      htmlFor="ano"
                    >
                      Ano
                    </label>
                    <select
                      id="ano"
                      className="bg-[#F7F9FB] border border-[#E3E3E3] rounded-[8px] px-3 py-2 outline-none w-full text-[#004A80]"
                      defaultValue=""
                      onChange={(e) => {
                        setCurrentPage(1);
                        setAno(e.target.value);
                      }}
                    >
                      <option value="" disabled hidden>Selecione o ano</option>
                      {anosUnicos.map((a) => (
                        <option key={a} value={a}>
                          {a}
                        </option>
                      ))}
                    </select>
                  </div> */}
										<div className='flex flex-col flex-1 min-w-0'>
											<label
												className='text-[#004A80] font-semibold mb-1 min-w-[36px]'
												htmlFor='status'>
												Status
											</label>
											<select
												id='ativo'
												className='bg-[#F7F9FB] border border-[#E3E3E3] rounded-[8px] px-3 py-2 outline-none w-full text-[#004A80]'
												value={ativo}
												onChange={(e) => {
													setCurrentPage(1);
													setAtivo(e.target.value);
												}}>
												<option
													value=''
													disabled
													hidden>
													Selecione o status
												</option>
												{statusUnicos.map((s) => (
													<option
														key={s.value}
														value={s.value}>
														{s.label}
													</option>
												))}
											</select>
										</div>
									</div>
									{/* Linha 2 - Desktop (mantida) */}
									<div className='hidden sm:flex sm:flex-row gap-6 w-full'>
										<div className='flex flex-col flex-1'>
											<label
												className='text-[#004A80] font-semibold mb-1 min-w-[80px]'
												htmlFor='categoria'>
												Categoria
											</label>
											<select
												id='categoria'
												className='bg-[#F7F9FB] border border-[#E3E3E3] rounded-[8px] px-3 py-2 outline-none w-full text-[#004A80]'
												value={categoria}
												onChange={(e) => {
													setCurrentPage(1);
													setCategoria(
														e.target.value
													);
												}}>
												<option
													value=''
													disabled
													hidden>
													Selecione a Categoria
												</option>
												{/* Opções da API */}
												{categoriaList.map((cat) => (
													<option
														key={cat.id}
														value={cat.nome}>
														{cat.nome}
													</option>
												))}
												{/* Opções únicas dos documentos (mantém para compatibilidade) */}
												{categoriasUnicas.map((c) => (
													<option
														key={c}
														value={c}>
														{c}
													</option>
												))}
											</select>
										</div>
										<div className='flex flex-col flex-1'>
											<label
												className='text-[#004A80] font-semibold mb-1 min-w-[56px]'
												htmlFor='tema'>
												Tema
											</label>
											<select
												id='tema'
												className='bg-[#F7F9FB] border border-[#E3E3E3] rounded-[8px] px-3 py-2 outline-none w-full text-[#004A80]'
												value={tema}
												onChange={(e) => {
													setCurrentPage(1);
													setTema(e.target.value);
												}}>
												<option
													value=''
													disabled
													hidden>
													Selecione o tema
												</option>
												{/* Opções da API */}
												{temaList.map((t) => (
													<option
														key={t.id}
														value={t.nome}>
														{t.nome}
													</option>
												))}
												{/* Opções únicas dos documentos (mantém para compatibilidade) */}
												{temasUnicos.map((t) => (
													<option
														key={t}
														value={t}>
														{t}
													</option>
												))}
											</select>
										</div>
										<div className='flex flex-col flex-1'>
											<label
												className='text-[#004A80] font-semibold mb-1 min-w-[56px]'
												htmlFor='localDocumento'>
												Local do Documento
											</label>
											<select
												id='localDocumento'
												className='bg-[#F7F9FB] border border-[#E3E3E3] rounded-[8px] px-3 py-2 outline-none w-full text-[#004A80]'
												value={localDocumento}
												onChange={(e) => {
													setCurrentPage(1);
													setLocalDocumento(
														e.target.value
													);
												}}>
												<option
													value=''
													disabled
													hidden>
													Selecione o local
												</option>
												{repoList
													?.map((obj) => obj?.nome)
													?.map((repo) => (
														<option
															key={repo}
															value={repo}>
															{repo}
														</option>
													))}
											</select>
										</div>
										<div className='flex flex-col flex-1'>
											<label
												className='text-[#004A80] font-semibold mb-1 min-w-[56px]'
												htmlFor='repositorio'>
												Repositório
											</label>
											<select
												id='publico'
												className='bg-[#F7F9FB] border border-[#E3E3E3] rounded-[8px] px-3 py-2 outline-none w-full text-[#004A80]'
												value={publico}
												onChange={(e) => {
													setCurrentPage(1);
													setPublico(e.target.value);
												}}>
												<option
													value=''
													disabled
													hidden>
													Selecione o repositório
												</option>
												<option value='true'>
													Público
												</option>
												<option value='false'>
													Privado
												</option>
											</select>
										</div>
									</div>
									<div className='flex justify-center mt-6 gap-4'>
										<button
											type='button'
											onClick={resetFilters}
											className='bg-[#FCFCFC] px-6 py-2 rounded-[8px] text-[#757575] font-semibold shadow-lg border border-[#E3E3E3] hover:bg-[#f0f4f8] hover:text-[#004A80] transition-colors'>
											Limpar Filtros
										</button>
										{/* <button
                    type="submit"
                    className="bg-[#FCFCFC] px-6 py-2 rounded-[8px] text-[#757575] font-semibold shadow-lg border border-[#E3E3E3] hover:bg-[#004A80] hover:text-white transition-colors"
                  >
                    Pesquisar
                  </button> */}
									</div>
								</form>
							</div>
						</div>
					</div>

					<div className='mx-auto w-[90%] sm:w-[95%] items-center max-w-[1136px] mb-16'>
						<div className='h-[1px] mt-[28px] mb-8 bg-[#E3E3E3]'></div>
						<div className='flex justify-end mb-2 w-full text-[#757575]'>
							<button
								className='bg-white border sm:text-xl text-[12px] sm:px-4 sm:py-2 px-3 py-1 rounded-[8px] hover:bg-[#004A80] hover:text-white flex items-center gap-2 shadow'
								onClick={() => navigate('/gestao-arquivos')}>
								<span className='sm:text-3xl text-lg  transition-all duration-100'>
									+
								</span>{' '}
								Adicionar Arquivo
							</button>
						</div>

						{/* Layout Desktop (Tabela) */}

						<div className='hidden sm:block bg-[#F4F4F4] shadow px-2 w-full overflow-x-auto rounded-md'>
							<table className=' w-full  border-separate border-spacing-y-2 border-spacing-0 transition-all duration-300'>
								<thead className='transition-all duration-300'>
									<tr className=' transition-all duration-300 rounded-t-2xl'>
										<th className='py-3 px-4 text-left bg-[#004A80] rounded-l-md text-white font-semibold'>
											Nome do Documento
										</th>
										<th className='py-3 px-4 text-left bg-[#004A80] text-white font-semibold'>
											Local do Documento
										</th>
										<th className='py-3 px-4 text-left bg-[#004A80] text-white font-semibold'>
											Mês
										</th>
										<th className='py-3 px-4 text-left bg-[#004A80] text-white font-semibold'>
											Ano
										</th>
										<th className='py-3 px-4 text-left bg-[#004A80] text-white font-semibold'>
											Status
										</th>
										<th className='py-3 px-4 text-left bg-[#004A80] text-white font-semibold'>
											Repositório
										</th>
										<th className='py-3 px-4 text-left bg-[#004A80] rounded-r-md text-white font-semibold'>
											Ação
										</th>
									</tr>
								</thead>
								{/* {console.log(documentosFiltrados?.slice((currentPage - 1) * pageSize, currentPage * pageSize)?} */}

								<tbody className=''>
									{documentosFiltrados.map((doc, idx) => (
										<tr
											key={doc.id}
											className={`${
												idx % 2 === 0 ? ' ' : ''
											}`}>
											<td
												className={`${
													idx % 2 === 0
														? 'bg-white '
														: ''
												} py-4 px-4 rounded-l-md`}>
												{doc.arquivo_url ? (
													<a
														href={doc.arquivo_url}
														target='_blank'
														rel='noopener noreferrer'
														className='text-[#204A80] hover:underline'>
														{doc.nome || doc.titulo}
													</a>
												) : (
													doc.nome || doc.titulo
												)}
											</td>

											<td
												className={`${
													idx % 2 === 0
														? 'bg-white'
														: ''
												} py-4 px-4 text-[#757575] max-w-[300px] `}>
												{doc?.repositorios?.length === 0
													? 'Sem Repositórios'
													: doc?.repositorios.map(
															(item, index) => (
																<span
																	key={index}
																	className='inline-block'>
																	{item}
																	{index <
																		doc
																			.repositorios
																			.length -
																			1 &&
																		'; '}
																</span>
															)
													  )}
											</td>

											<td
												className={`${
													idx % 2 === 0
														? 'bg-white '
														: ''
												} py-4 px-4  text-[#757575]`}>
												{isNaN(doc.mes)
													? doc.mes
													: mesNumeroParaNome(
															doc.mes
													  )}
											</td>
											<td
												className={`${
													idx % 2 === 0
														? 'bg-white '
														: ''
												} py-4 px-4  text-[#757575]`}>
												{doc.ano}
											</td>
											<td
												className={`py-4 px-4 ${
													idx % 2 === 0
														? 'bg-white '
														: ''
												} font-semibold ${
													doc.ativo
														? 'text-[#757575]'
														: 'text-[#C23C3C]'
												}`}>
												{doc.ativo
													? 'Ativo'
													: 'Inativo'}
											</td>
											<td
												className={`py-4 ${
													idx % 2 === 0
														? 'bg-white '
														: ''
												} px-4`}>
												<span
													style={{
														color: '#757575',
													}}>
													{' '}
													{doc.publico ? (
														'Público'
													) : (
														<span
															style={{
																color: '#004A80',
															}}>
															Privado
														</span>
													)}
												</span>
											</td>
											<td
												className={`py-4 ${
													idx % 2 === 0
														? 'bg-white '
														: ''
												} rounded-r-md px-4`}>
												<button
													className='border border-[#E3E3E3] rounded-[6px] px-2 py-1 bg-white flex items-center justify-center'
													onClick={() => {
														navigate(
															`/gestao-editar-arquivos/${doc.id}`
														);
													}}>
													<img
														src={lapisIcone}
														alt='Editar'
														className='w-4 h-4'
													/>
												</button>
											</td>
										</tr>
									))}
								</tbody>
							</table>
							{documentosFiltrados.length === 0 ? (
								<div className='flex justify-center font-semibold text-[#b8b8b8] content-center text-xl items-center w-[100%] h-screen max-h-[400px]'>
									Nenhum documento encontrado...
								</div>
							) : null}
						</div>

						{/* Layout Mobile (Cards) */}
						<div className='block sm:hidden space-y-4'>
							{documentosFiltrados.map((doc, idx) => {
								const bgClass =
									idx % 2 === 0 ? 'bg-[#F4F4F4]' : 'bg-white';
								return (
									<div
										key={doc.id}
										className={`${bgClass} shadow rounded-2xl p-4`}>
										<h2 className='font-bold text-lg'>
											{doc.arquivo_url ? (
												<a
													href={doc.arquivo_url}
													target='_blank'
													rel='noopener noreferrer'
													className='text-[#204A80] hover:underline'>
													{doc.nome || doc.titulo}
												</a>
											) : (
												doc.nome || doc.titulo
											)}
										</h2>
										<p>
											<span className='font-semibold'>
												Local:
											</span>{' '}
											{doc?.repositorios?.length === 0
												? 'Sem Repositórios'
												: doc?.repositorios?.length ===
												  1
												? doc?.repositorios[0]
												: doc?.repositorios?.join(', ')}
										</p>
										<p>
											<span className='font-semibold'>
												Ano:
											</span>{' '}
											{doc.ano || '—'}
										</p>
										<p>
											<span className='font-semibold'>
												Status:
											</span>{' '}
											{doc.ativo ? (
												<span className='text-green-600'>
													Ativo
												</span>
											) : (
												<span className='text-red-600'>
													Inativo
												</span>
											)}
										</p>
										<p>
											<span className='font-semibold'>
												Repositório:
											</span>{' '}
											{doc.publico ? (
												'Público'
											) : (
												<span
													style={{
														color: '#004A80',
													}}>
													Privado
												</span>
											)}
										</p>
										<div className='flex justify-end mt-2'>
											<button
												className='border border-[#E3E3E3] rounded-lg p-2 bg-white flex items-center justify-center'
												onClick={() =>
													navigate(
														`/gestao-editar-arquivos/${doc.id}`
													)
												}>
												<img
													src={lapisIcone}
													alt='Editar'
													className='w-4 h-4'
												/>
											</button>
										</div>
									</div>
								);
							})}
							{documentosFiltrados.length === 0 ? (
								<div className='flex justify-center font-semibold text-[#b8b8b8] content-center text-lg items-center w-[100%] h-screen max-h-[200px]'>
									Nenhum documento encontrado...
								</div>
							) : null}
						</div>
						{/* {console.log(documentosFiltrados)} */}
						{/* Controles de Paginação */}
						{documentosFiltrados.length > 0 && (
							<div className='flex flex-col sm:flex-row justify-between items-center mt-4 gap-4'>
								{/* Informações da paginação */}
								{/* {console.log(documentosFiltrados)} */}

								<div className='text-sm text-[#757575]'>
									Mostrando {(currentPage - 1) * pageSize + 1}{' '}
									a{' '}
									{(currentPage - 1) * pageSize +
										1 +
										documentosFiltrados.length -
										1}{' '}
									de {totalCount} resultados
								</div>
								{/* Controles de página */}
								<div className='flex items-center gap-2'>
									<button
										onClick={() => handlePageChange(1)}
										disabled={currentPage === 1}
										className={`px-3 py-1 rounded border ${
											currentPage === 1
												? 'bg-gray-100 text-gray-400 cursor-not-allowed'
												: 'bg-white text-[#204A80] border-[#E3E3E3] hover:bg-[#F7F9FB]'
										}`}>
										&laquo;
									</button>
									<button
										onClick={() =>
											handlePageChange(currentPage - 1)
										}
										disabled={currentPage === 1}
										className={`px-3 py-1 rounded border ${
											currentPage === 1
												? 'bg-gray-100 text-gray-400 cursor-not-allowed'
												: 'bg-white text-[#204A80] border-[#E3E3E3] hover:bg-[#F7F9FB]'
										}`}>
										&lsaquo;
									</button>
									{Array.from(
										{ length: Math.min(5, totalPages) },
										(_, i) => {
											let pageNumber;

											if (totalPages <= 5) {
												pageNumber = i + 1;
											} else if (currentPage <= 3) {
												pageNumber = i + 1;
											} else if (
												currentPage >=
												totalPages - 2
											) {
												pageNumber = totalPages - 4 + i;
											} else {
												pageNumber =
													currentPage - 2 + i;
											}
											return (
												<button
													key={pageNumber}
													onClick={() =>
														handlePageChange(
															pageNumber
														)
													}
													className={`px-3 py-1 rounded border ${
														currentPage ===
														pageNumber
															? 'bg-[#204A80] text-white border-[#204A80]'
															: 'bg-white text-[#204A80] border-[#E3E3E3] hover:bg-[#F7F9FB]'
													}`}>
													{pageNumber}
												</button>
											);
										}
									)}
									<button
										onClick={() =>
											handlePageChange(currentPage + 1)
										}
										disabled={currentPage === totalPages}
										className={`px-3 py-1 rounded border ${
											currentPage === totalPages
												? 'bg-gray-100 text-gray-400 cursor-not-allowed'
												: 'bg-white text-[#204A80] border-[#E3E3E3] hover:bg-[#F7F9FB]'
										}`}>
										&rsaquo;
									</button>
									<button
										onClick={() =>
											handlePageChange(totalPages)
										}
										disabled={currentPage === totalPages}
										className={`px-3 py-1 rounded border ${
											currentPage === totalPages
												? 'bg-gray-100 text-gray-400 cursor-not-allowed'
												: 'bg-white text-[#204A80] border-[#E3E3E3] hover:bg-[#F7F9FB]'
										}`}>
										&raquo;
									</button>
								</div>
								<div className='flex items-center gap-2'>
									<span className='text-sm text-[#757575]'>
										Itens por página:
									</span>
									<select
										value={pageSize}
										onChange={(e) =>
											handlePageSizeChange(
												parseInt(e.target.value)
											)
										}
										className='border border-[#E3E3E3] rounded px-2 py-1 text-sm'>
										<option value={5}>5</option>
										<option value={10}>10</option>
										<option value={20}>20</option>
										<option value={50}>50</option>
									</select>
								</div>
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
}
